import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { 
  Shield, UserPlus, Trash2, Save, Search, Users, 
  CheckCircle, XCircle, Settings, Eye, MessageSquare,
  Trophy, BarChart3, X
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Permission definitions with labels and icons
const PERMISSIONS = [
  { key: 'can_manage_submissions', label: 'Manage Submissions', icon: CheckCircle, description: 'Approve/reject video submissions' },
  { key: 'can_manage_users', label: 'Manage Users', icon: Users, description: 'Ban/unban users' },
  { key: 'can_manage_challenges', label: 'Manage Challenges', icon: Settings, description: 'Create and edit challenges' },
  { key: 'can_pick_winners', label: 'Pick Winners', icon: Trophy, description: 'Select challenge winners' },
  { key: 'can_view_analytics', label: 'View Analytics', icon: BarChart3, description: 'Access dashboard analytics' },
  { key: 'can_manage_feedback', label: 'Manage Feedback', icon: MessageSquare, description: 'Respond to user feedback' }
];

const PermissionToggle = ({ permission, enabled, onChange }) => {
  const Icon = permission.icon;
  
  return (
    <div 
      onClick={() => onChange(!enabled)}
      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
        enabled 
          ? 'bg-green-500/20 border-green-500/50 hover:bg-green-500/30' 
          : 'bg-gray-800/50 border-gray-700 hover:bg-gray-700/50'
      }`}
    >
      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
        enabled ? 'bg-green-500/30 text-green-400' : 'bg-gray-700 text-gray-500'
      }`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1">
        <p className={`text-sm font-medium ${enabled ? 'text-green-400' : 'text-gray-400'}`}>
          {permission.label}
        </p>
        <p className="text-xs text-gray-500">{permission.description}</p>
      </div>
      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
        enabled ? 'border-green-500 bg-green-500' : 'border-gray-600'
      }`}>
        {enabled && <CheckCircle className="w-3 h-3 text-white" />}
      </div>
    </div>
  );
};

const AddModeratorModal = ({ users, moderators, token, onClose, onSuccess }) => {
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [permissions, setPermissions] = useState({
    can_manage_submissions: false,
    can_manage_users: false,
    can_manage_challenges: false,
    can_pick_winners: false,
    can_view_analytics: false,
    can_manage_feedback: false
  });
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Filter out users who are already moderators
  const availableUsers = useMemo(() => {
    const safeUsers = Array.isArray(users) ? users : [];
    const safeModerators = Array.isArray(moderators) ? moderators : [];
    const modUserIds = safeModerators.map(m => m.user_id);
    return safeUsers.filter(u => 
      !modUserIds.includes(u.id) && 
      !u.is_admin &&
      (search === '' || 
        u.twitch_username?.toLowerCase().includes(search.toLowerCase()) ||
        u.display_name?.toLowerCase().includes(search.toLowerCase()))
    );
  }, [users, moderators, search]);

  const handleSave = async () => {
    if (!selectedUser) {
      toast.error('Please select a user');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/admin/moderators`, {
        user_id: selectedUser.id,
        permissions,
        notes: notes || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${selectedUser.display_name} is now a moderator!`);
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add moderator');
    } finally {
      setSaving(false);
    }
  };

  const toggleAllPermissions = (enable) => {
    const newPerms = {};
    PERMISSIONS.forEach(p => newPerms[p.key] = enable);
    setPermissions(newPerms);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-xl border border-purple-500/30 bg-gray-900 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700 bg-gradient-to-r from-purple-900/30 to-transparent">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                ADD MODERATOR
              </h2>
              <p className="text-xs text-gray-400">Select a user and assign permissions</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="p-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* User Selection */}
          <div className="mb-6">
            <Label className="text-gray-300 mb-2 block">Select User</Label>
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input
                placeholder="Search users..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-gray-800 border-gray-700"
              />
            </div>
            
            {selectedUser ? (
              <div className="flex items-center gap-3 p-3 bg-purple-500/20 border border-purple-500/50 rounded-lg">
                <img 
                  src={selectedUser.profile_image_url || '/default-avatar.png'} 
                  alt="" 
                  className="w-10 h-10 rounded-full"
                />
                <div className="flex-1">
                  <p className="text-white font-medium">{selectedUser.display_name}</p>
                  <p className="text-xs text-gray-400">@{selectedUser.twitch_username}</p>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setSelectedUser(null)}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-700 bg-gray-800/50">
                {availableUsers.length === 0 ? (
                  <p className="text-gray-500 text-sm p-4 text-center">No users found</p>
                ) : (
                  availableUsers.slice(0, 20).map(user => (
                    <div
                      key={user.id}
                      onClick={() => setSelectedUser(user)}
                      className="flex items-center gap-3 p-3 hover:bg-gray-700/50 cursor-pointer border-b border-gray-700/50 last:border-0"
                    >
                      <img 
                        src={user.profile_image_url || '/default-avatar.png'} 
                        alt="" 
                        className="w-8 h-8 rounded-full"
                      />
                      <div>
                        <p className="text-white text-sm">{user.display_name}</p>
                        <p className="text-xs text-gray-500">@{user.twitch_username}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Permissions */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-gray-300">Permissions</Label>
              <div className="flex gap-2">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => toggleAllPermissions(true)}
                  className="text-xs text-green-400 hover:text-green-300"
                >
                  Select All
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => toggleAllPermissions(false)}
                  className="text-xs text-gray-400 hover:text-gray-300"
                >
                  Clear All
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {PERMISSIONS.map(perm => (
                <PermissionToggle
                  key={perm.key}
                  permission={perm}
                  enabled={permissions[perm.key]}
                  onChange={(val) => setPermissions(prev => ({ ...prev, [perm.key]: val }))}
                />
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="mb-4">
            <Label className="text-gray-300 mb-2 block">Notes (optional)</Label>
            <Input
              placeholder="Add notes about this moderator..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="bg-gray-800 border-gray-700"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-700 bg-gray-800/50">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleSave}
            disabled={!selectedUser || saving}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Adding...' : 'Add Moderator'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const ModeratorCard = ({ moderator, token, onUpdate, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [permissions, setPermissions] = useState(moderator.permissions || {});
  const [notes, setNotes] = useState(moderator.notes || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/moderators/${moderator.id}`, {
        permissions,
        notes: notes || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Moderator updated');
      setEditing(false);
      onUpdate();
    } catch (error) {
      toast.error('Failed to update moderator');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Remove ${moderator.display_name} as moderator?`)) return;
    
    try {
      await axios.delete(`${API}/admin/moderators/${moderator.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Moderator removed');
      onDelete();
    } catch (error) {
      toast.error('Failed to remove moderator');
    }
  };

  const activePermCount = Object.values(permissions).filter(Boolean).length;

  return (
    <Card className="bg-gray-900/80 border-gray-700 overflow-hidden">
      <CardHeader className="py-3 px-4 bg-gradient-to-r from-purple-900/20 to-transparent border-b border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src={moderator.profile_image_url || '/default-avatar.png'} 
              alt="" 
              className="w-10 h-10 rounded-full border-2 border-purple-500/50"
            />
            <div>
              <p className="text-white font-semibold">{moderator.display_name}</p>
              <p className="text-xs text-gray-500">@{moderator.twitch_username}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-purple-500/20 text-purple-400 text-xs rounded-full">
              {activePermCount} permission{activePermCount !== 1 ? 's' : ''}
            </span>
            {!editing && (
              <>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setEditing(true)}
                  className="text-gray-400 hover:text-white"
                >
                  <Settings className="w-4 h-4" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleDelete}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-4">
        {editing ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {PERMISSIONS.map(perm => (
                <PermissionToggle
                  key={perm.key}
                  permission={perm}
                  enabled={permissions[perm.key]}
                  onChange={(val) => setPermissions(prev => ({ ...prev, [perm.key]: val }))}
                />
              ))}
            </div>
            <div>
              <Label className="text-gray-400 text-xs mb-1 block">Notes</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add notes..."
                className="bg-gray-800 border-gray-700 text-sm"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
                Cancel
              </Button>
              <Button 
                size="sm" 
                onClick={handleSave}
                disabled={saving}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Save className="w-3 h-3 mr-1" />
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {PERMISSIONS.map(perm => (
                permissions[perm.key] && (
                  <span 
                    key={perm.key}
                    className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full flex items-center gap-1"
                  >
                    <perm.icon className="w-3 h-3" />
                    {perm.label}
                  </span>
                )
              ))}
              {activePermCount === 0 && (
                <span className="text-gray-500 text-xs">No permissions assigned</span>
              )}
            </div>
            {moderator.notes && (
              <p className="text-xs text-gray-500 mt-2 italic">"{moderator.notes}"</p>
            )}
            <p className="text-xs text-gray-600 mt-2">
              Added by {moderator.added_by} • {new Date(moderator.added_at).toLocaleDateString()}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const ModeratorsTab = ({ users = [], token, onRefresh }) => {
  const [moderators, setModerators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  const fetchModerators = async () => {
    try {
      const res = await axios.get(`${API}/admin/moderators`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModerators(res.data || []);
    } catch (error) {
      console.error('Failed to fetch moderators:', error);
      toast.error('Failed to load moderators');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchModerators();
    }
  }, [token]);

  const safeModerators = Array.isArray(moderators) ? moderators : [];

  const handleModeratorAdded = () => {
    setShowAddModal(false);
    fetchModerators();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
            <Shield className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Orbitron, sans-serif' }}>
              MODERATORS
            </h2>
            <p className="text-sm text-gray-500">
              {safeModerators.length} moderator{safeModerators.length !== 1 ? 's' : ''} assigned
            </p>
          </div>
        </div>
        <Button 
          onClick={() => setShowAddModal(true)}
          className="bg-purple-600 hover:bg-purple-700"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Add Moderator
        </Button>
      </div>

      {/* Moderators List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full"></div>
        </div>
      ) : safeModerators.length === 0 ? (
        <Card className="bg-gray-900/50 border-gray-700">
          <CardContent className="py-12 text-center">
            <Shield className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-400 mb-2">No Moderators Yet</h3>
            <p className="text-gray-500 text-sm mb-4">
              Add moderators to help manage your platform
            </p>
            <Button 
              onClick={() => setShowAddModal(true)}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Add First Moderator
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {safeModerators.map(mod => (
            <ModeratorCard
              key={mod.id}
              moderator={mod}
              token={token}
              onUpdate={fetchModerators}
              onDelete={fetchModerators}
            />
          ))}
        </div>
      )}

      {/* Permission Legend */}
      <Card className="bg-gray-900/30 border-gray-800">
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-sm text-gray-400 flex items-center gap-2">
            <Eye className="w-4 h-4" />
            Permission Reference
          </CardTitle>
        </CardHeader>
        <CardContent className="py-3 px-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {PERMISSIONS.map(perm => (
              <div key={perm.key} className="flex items-center gap-2 text-xs text-gray-500">
                <perm.icon className="w-3 h-3" />
                <span>{perm.label}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Add Modal */}
      {showAddModal && (
        <AddModeratorModal
          users={users}
          moderators={moderators}
          token={token}
          onClose={() => setShowAddModal(false)}
          onSuccess={handleModeratorAdded}
        />
      )}
    </div>
  );
};

export default ModeratorsTab;
